/* =============================================================================
 * Virtual Try-On Experience (mg6foqrk) — standalone Try-On Studio engine.
 *
 * Reads the SSR `data-vto-payload` JSON (collection products + merchant props)
 * and drives the full inline step flow:
 *   Photo  → add a photo
 *   Products → browse the collection, build a multi-item look (tray)
 *   Result → AI preview of the whole look + per-item size + add to cart
 *
 * Backend (Shopify app proxy, base /apps/spectrum):
 *   POST /vto/upload-url   multipart `photo` → { success, data:{ publicUrl, r2Key } }
 *   POST /vto/generate     buildGenerateBody JSON → { success, data:{ resultImageUrl, validationWarnings } }
 *   GET  /vto/rate-limit   ?limit=N&cid=… → { remaining, limit, resetAt }
 *
 * Theme-agnostic: all styling lives in the snippet stylesheet (--sai-* tokens).
 * Container-scoped: every DOM op is rooted at this custom element; no globals.
 * ============================================================================= */
;(() => {
  if (window.__sai_mg6foqrk_initialized__) return
  window.__sai_mg6foqrk_initialized__ = true

  const SNIPPET_ID = 'mg6foqrk'
  const TAG = 'sai-mg6foqrk'
  const FEATURE_SLUG = 'virtual_try_on'
  const MAX_PHOTO_BYTES = 10 * 1024 * 1024
  const GENERATE_TIMEOUT_MS = 120000
  const MAX_GARMENTS = 6
  // Products are revealed a page at a time via infinite scroll (IntersectionObserver).
  const PAGE_SIZE = 12
  // When a collection holds more products than the SSR payload carries, the
  // engine lazy-fetches the rest from /collections/{handle}/products.json. 250
  // is Shopify's max page size for that endpoint.
  const SERVER_PAGE_SIZE = 250
  const ACCEPT = 'image/jpeg,image/png,image/webp'
  const PROCESSING_MESSAGES = [
    'Styling your complete look…',
    'Fitting each piece…',
    'Perfecting the details…',
    'Almost there…',
  ]

  // ── Pure helpers ──────────────────────────────────────────────────────────
  function noop() {}

  function el(tag, className, opts) {
    const node = document.createElement(tag)
    if (className) node.className = className
    if (opts) {
      if (opts.text != null) node.textContent = opts.text
      if (opts.html != null) node.innerHTML = opts.html
      for (const k in opts) {
        if (k === 'text' || k === 'html') continue
        if (Object.prototype.hasOwnProperty.call(opts, k)) node.setAttribute(k, opts[k])
      }
    }
    return node
  }

  // Static, author-controlled SVG markup — no user data interpolated.
  function svg(markup, size) {
    const s = size || 18
    const span = el('span', null, {
      html: `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" focusable="false" aria-hidden="true">${markup}</svg>`,
    })
    span.style.display = 'inline-flex'
    return span
  }
  const ICON_UPLOAD =
    '<path d="M12 16V4M12 4l-4 4M12 4l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
  const ICON_CAMERA =
    '<path d="M3 8.5A1.5 1.5 0 0 1 4.5 7H7l1-1.5h8L17 7h2.5A1.5 1.5 0 0 1 21 8.5V18a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="12" cy="12.5" r="3.2" stroke="currentColor" stroke-width="1.6"/>'
  const ICON_IMAGE =
    '<rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.6"/><circle cx="8.5" cy="9.5" r="1.6" stroke="currentColor" stroke-width="1.4"/><path d="M4.5 18l4.5-4.5 3 3 4-4 3.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
  const ICON_ZOOM =
    '<circle cx="11" cy="11" r="6.25" stroke="currentColor" stroke-width="1.7"/><path d="M20.5 20.5l-4.2-4.2M11 8.6v4.8M8.6 11h4.8" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>'
  const ICON_CLOSE =
    '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'

  // Append an <img>, or a neutral placeholder icon when the URL is missing.
  function appendMedia(node, url, alt, iconSize) {
    if (url) {
      node.appendChild(el('img', null, { src: url, alt: alt || '', loading: 'lazy' }))
    } else {
      const ph = el('span', 'sai-mg6foqrk__noimg')
      ph.appendChild(svg(ICON_IMAGE, iconSize || 22))
      node.appendChild(ph)
    }
  }
  const ICON_SPARKLE =
    '<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>'
  const ICON_BACK =
    '<path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
  // Spectrum logo (mark + wordmark) — matches the existing VTO snippet
  // (o1uear1c). fill=currentColor so the watermark CSS controls its colour; the
  // mark path uses evenodd to keep its interior cutouts. viewBox is the source
  // asset's intrinsic 423×64 box.
  const SPECTRUM_LOGO =
    '<svg viewBox="0 0 423 64" fill="currentColor" focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M79.52 26.24C79.52 31.952 108.992 23.36 108.992 39.44C108.992 43.328 107.504 49.952 92.816 49.952H86.672C71.984 49.952 70.496 41.888 70.496 38H79.568C79.568 39.008 80.192 41.36 84.224 41.36H96.704C99.248 41.36 99.92 40.448 99.92 39.44C99.92 33.296 70.4 42.368 70.4 26.24C70.4 22.304 71.888 15.68 86.624 15.68H92.72C107.456 15.68 108.944 23.744 108.944 27.68H99.824C99.824 26.624 99.248 24.32 95.168 24.32H82.688C80.048 24.32 79.52 25.184 79.52 26.24ZM136.004 15.68C142.196 15.68 152.372 16.64 152.372 28.496C152.372 40.352 142.196 41.312 136.004 41.312H120.596V49.952H111.476V15.68H136.004ZM136.772 32.72C141.62 32.72 142.82 31.184 142.82 28.496C142.82 25.808 141.62 24.32 136.772 24.32H120.596V32.72H136.772ZM154.801 15.68H192.097V24.176H163.873V28.928H192.097V36.944H163.873V41.456H192.097V49.952H154.801V15.68ZM210.118 24.32C205.27 24.32 204.118 28.496 204.118 32.816C204.118 37.184 205.27 41.36 210.118 41.36H222.598C226.198 41.36 227.686 39.536 228.358 35.936H238.006C236.518 48.752 225.814 49.952 220.102 49.952H212.614C206.47 49.952 194.518 48.608 194.518 32.816C194.518 17.072 206.47 15.68 212.614 15.68H220.102C225.814 15.68 236.518 16.88 238.006 29.696H228.358C227.926 26.384 226.198 24.32 222.598 24.32H210.118ZM238.499 24.32V15.68H273.011V24.32H260.147V49.952H251.075V24.32H238.499ZM316.346 28.496C316.346 36.032 312.218 39.152 307.658 40.448L318.074 49.952H305.642L296.618 41.312H284.57V49.952H275.45V15.68H299.978C306.17 15.68 316.346 16.64 316.346 28.496ZM284.57 24.32V32.72H300.746C305.594 32.72 306.794 31.184 306.794 28.496C306.794 25.808 305.594 24.32 300.746 24.32H284.57ZM353.148 32.816V15.68H362.268V32.816C362.268 48.608 350.316 49.952 344.172 49.952H336.684C330.54 49.952 318.588 48.608 318.588 32.816V15.68H327.708V32.816C327.708 37.184 329.34 41.36 334.188 41.36H347.148C351.996 41.36 353.148 37.184 353.148 32.816ZM392.305 40.256L404.209 15.68H418.993V49.952H409.873V25.136L398.113 49.952H386.449L374.689 25.136V49.952H365.617V15.68H380.353L392.305 40.256Z"/><path fill-rule="evenodd" clip-rule="evenodd" d="M22.3286 0C22.963 0.0413433 23.6023 0.0375721 24.227 0.0771484C30.6625 0.484851 36.9437 3.62804 41.8969 7.42578C42.6755 8.02258 44.189 9.23645 44.8432 9.91504L33.6713 20.9541C34.917 22.2163 36.1344 23.3551 37.2875 24.7178C42.0091 30.2963 45.6281 37.315 44.725 44.6582C44.2491 48.7077 42.497 52.5253 39.6996 55.6084C37.4206 58.1146 34.5303 60.072 31.3666 61.4346C29.8672 62.08 28.2402 62.6334 26.7104 63.2344C25.4952 63.7114 23.9291 63.9031 22.6293 63.9932C22.0023 64.0019 21.2671 64.0086 20.6411 63.9688C14.2754 63.5625 8.12043 60.4825 3.19867 56.7715C2.35948 56.1385 0.748809 54.8513 0.0502322 54.1064C1.5444 52.6952 3.04881 51.1643 4.5141 49.7158L11.2192 43.0918C8.80053 40.7773 6.67142 38.428 4.82367 35.666C1.55523 30.7804 -0.611089 24.9787 0.154724 19.1328C0.66123 15.0635 2.45637 11.2375 5.30219 8.16406C8.48682 4.75101 12.2397 2.76515 16.7123 1.28613C18.724 0.620894 20.1163 0.121257 22.3286 0ZM15.3735 39.0264C14.0337 40.3484 12.5865 41.7287 11.2875 43.0762C15.1019 46.5593 19.3059 49.3135 24.2338 51.1924C26.5689 52.036 30.3685 53.0813 32.9037 52.915C33.2021 52.7202 33.3241 52.6049 33.5307 52.3193C33.5239 50.3853 33.1699 48.7255 32.558 46.8789C30.7096 41.2989 27.0028 36.288 22.643 32.2188C22.5625 32.1437 22.5297 32.0892 22.4203 32.0732L15.3735 39.0264ZM11.9174 11.0889C11.5924 11.1919 11.3827 11.5323 11.2045 11.8008L11.2319 11.8594C11.3608 12.1432 11.3803 12.2916 11.3842 12.5986C11.3904 13.0896 11.4683 13.5641 11.5483 14.0479C11.6866 14.8705 11.8771 15.6854 12.1176 16.4863C13.6202 21.4711 16.6698 26.0836 20.311 29.9053C20.8425 30.4632 21.8645 31.5401 22.4477 31.9443C22.9289 31.5725 23.8764 30.5872 24.3383 30.1289L27.5688 26.9336L31.4399 23.1152C32.0844 22.4786 33.0103 21.6143 33.5873 20.958C32.8467 20.2443 32.0551 19.594 31.2495 18.9482C26.0261 14.7612 18.8613 11.1838 11.9174 11.0889Z"/></svg>'

  function readPayload(root) {
    const node = root.querySelector('script[type="application/json"][data-vto-payload]')
    if (!node) return null
    try {
      return JSON.parse(node.textContent || '')
    } catch (_) {
      return null
    }
  }

  function absUrl(url) {
    if (!url) return url
    if (/^https?:\/\//i.test(url)) return url
    // Shopify's image_url filter emits protocol-relative URLs (//cdn.shopify…).
    // The /vto/generate server validates absolute http(s) URLs, so upgrade these
    // to https — returning them as-is fails validation ("Validation failed").
    if (url.indexOf('//') === 0) return `https:${url}`
    try {
      return new URL(url, window.location.origin).href
    } catch (_) {
      return url
    }
  }

  // Format integer cents → money string. Prefers the Spectrum SDK formatter,
  // which honours the store's money_format (and so the active presentment
  // currency on a single-currency theme); falls back to a currency-aware Intl
  // format so prices still render where the SDK isn't present (e.g. tests).
  // Returns '' for a missing price so callers can skip rendering the node.
  function formatMoney(cents, moneyFormat, currency) {
    if (cents == null || cents === '') return ''
    const sdk = window.Spectrum && window.Spectrum.money
    if (sdk && typeof sdk.format === 'function') {
      try {
        return sdk.format(cents, moneyFormat || undefined)
      } catch (_) {
        /* fall through to Intl */
      }
    }
    const value = Number(cents) / 100
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
      }).format(value)
    } catch (_) {
      return value.toFixed(2)
    }
  }

  function distinctId() {
    const fromSdk =
      window.__spectrumAi?.analytics &&
      typeof window.__spectrumAi.analytics.getDistinctId === 'function'
        ? window.__spectrumAi.analytics.getDistinctId()
        : null
    if (fromSdk) return fromSdk
    const m = document.cookie.match(/(?:^|;\s*)__spectrum_fp=([^;]+)/)
    return m ? m[1] : ''
  }

  // Option groups for a product — one entry per option (Colour, Size, Material,
  // Length, …), fully generic with NO option-name matching. For each option:
  //   values[]   distinct values, in first-seen order
  //   available  rolled up against the current selection of the OTHER options,
  //              so impossible combinations are disabled
  //   image      first variant image carrying that value (drives swatches)
  //   hasImages  whether any value has an image → render the group as swatches
  // `selection` is an array of chosen values aligned to product.options (any
  // entry may be null for "not yet chosen").
  function productOptionGroups(product, selection) {
    const opts = product?.options
    if (!Array.isArray(opts) || !opts.length) return []
    const vs = product.variants || []
    const sel = selection || []
    const groups = []
    for (let idx = 0; idx < opts.length; idx++) {
      const name = typeof opts[idx] === 'string' ? opts[idx] : opts[idx]?.name || ''
      const seen = {}
      const values = []
      for (let i = 0; i < vs.length; i++) {
        const v = vs[i]
        const val = v.options?.[idx]
        if (val == null) continue
        if (!seen[val]) {
          seen[val] = { value: val, available: false, image: null }
          values.push(seen[val])
        }
        // A value is available iff some variant with that value is available AND
        // matches the currently-selected values of every OTHER option.
        let matchesOthers = true
        for (let j = 0; j < opts.length; j++) {
          if (j === idx) continue
          if (sel[j] != null && v.options?.[j] !== sel[j]) {
            matchesOthers = false
            break
          }
        }
        if (v.available && matchesOthers) seen[val].available = true
        if (!seen[val].image && v.imageUrl) seen[val].image = absUrl(v.imageUrl)
      }
      // Render as swatches only when this option drives the product photo —
      // i.e. its values map to MORE THAN ONE distinct image. Size/Material
      // merely inherit the colour's photo (one shared image), so they stay
      // pills. Generic: no option-name matching.
      const distinctImages = {}
      let imageCount = 0
      for (let k = 0; k < values.length; k++) {
        const img = values[k].image
        if (img && !distinctImages[img]) {
          distinctImages[img] = true
          imageCount++
        }
      }
      groups.push({ index: idx, name: name, values: values, hasImages: imageCount > 1 })
    }
    return groups
  }

  // First-available value for each option (falls back to the first value).
  // Returns an array aligned to product.options.
  function defaultSelection(product) {
    const groups = productOptionGroups(product, [])
    const sel = []
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i]
      const avail = g.values.filter((x) => x.available)[0]
      const v = (avail || g.values[0])?.value
      sel[g.index] = v == null ? null : v
    }
    return sel
  }

  // Image to show for a selection: the chosen value of the first image-bearing
  // option, else the product's featured image.
  function selectionImage(product, selection) {
    const groups = productOptionGroups(product, selection)
    const sel = selection || []
    for (let i = 0; i < groups.length; i++) {
      const g = groups[i]
      if (!g.hasImages) continue
      const hit = g.values.filter((x) => x.value === sel[g.index])[0]
      if (hit?.image) return hit.image
    }
    return product?.featuredImageUrl ? absUrl(product.featuredImageUrl) : null
  }

  // Resolve the variant for a (partial) selection array. Falls back to the
  // first available variant, then the first variant.
  function pickVariantBySelection(product, selection) {
    const vs = product.variants || []
    if (!vs.length) return null
    const sel = selection || []
    for (let i = 0; i < vs.length; i++) {
      const v = vs[i]
      let ok = true
      for (let j = 0; j < sel.length; j++) {
        if (sel[j] != null && v.options?.[j] !== sel[j]) {
          ok = false
          break
        }
      }
      if (ok) return v
    }
    for (let k = 0; k < vs.length; k++) if (vs[k].available) return vs[k]
    return vs[0]
  }

  // Normalise a Shopify /collections/{handle}/products.json product into the SSR
  // payload product shape the engine consumes. The legacy storefront JSON differs
  // from the Liquid payload in three ways that matter here:
  //   - prices are decimal strings ("19.99"), not integer cents;
  //   - product.options are { name, values } objects, not name strings;
  //   - variant option values live in option1/option2/option3, not an array.
  // Output must be byte-compatible with the Liquid payload product so cards,
  // detail, option groups, and the generate body all work unchanged.
  function toCents(price) {
    const n = Number(price)
    return Number.isFinite(n) ? Math.round(n * 100) : null
  }

  // Append a width hint to a Shopify CDN image URL (which already carries ?v=…),
  // matching the Liquid payload's `image_url: width: 800`.
  function sizedImageUrl(url, width) {
    if (!url) return null
    return url.indexOf('?') >= 0 ? `${url}&width=${width}` : `${url}?width=${width}`
  }

  function variantOptionValues(v) {
    const out = []
    if (v.option1 != null) out.push(v.option1)
    if (v.option2 != null) out.push(v.option2)
    if (v.option3 != null) out.push(v.option3)
    return out
  }

  function normalizeAjaxProduct(p) {
    const variants = (p.variants || []).slice(0, 50).map((v) => ({
      id: v.id,
      title: v.title,
      options: variantOptionValues(v),
      available: v.available !== false,
      price: toCents(v.price),
      imageUrl: sizedImageUrl(v.featured_image && v.featured_image.src, 800),
    }))
    const featuredRaw =
      p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || null
    const firstAvailable = variants.filter((v) => v.available)[0] || variants[0] || null
    return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: `/products/${p.handle || ''}`,
      type: p.product_type || '',
      // The legacy JSON endpoint doesn't expose taxonomy category; tray grouping
      // falls back to product type via categoryOf().
      category: null,
      available: variants.some((v) => v.available),
      price: firstAvailable ? firstAvailable.price : null,
      featuredImageUrl: sizedImageUrl(featuredRaw, 800),
      options: (p.options || []).map((o) => (typeof o === 'string' ? o : (o && o.name) || '')),
      variants,
    }
  }

  function garmentImage(item) {
    return absUrl(item.variant?.imageUrl || item.product?.featuredImageUrl)
  }

  // Category key for a product — the "collection" a selection belongs to. Used to
  // enforce one selection per category in the tray.
  function categoryOf(product) {
    return String((product && (product.type || product.category)) || '').toLowerCase()
  }

  // Order products so sold-out (available === false) ones land last, without the
  // reshuffle that re-sorting a partially-loaded list causes. While the
  // collection is still streaming in (loaded = false) sold-out items are held
  // back entirely — they're appended only once everything is loaded, so they
  // sort to the very end and the in-stock items above them never move. Stable:
  // order within each group is preserved; returns a new array.
  function orderProductsOosLast(products, loaded) {
    const inStock = products.filter((p) => p.available !== false)
    if (!loaded) return inStock
    const soldOut = products.filter((p) => p.available === false)
    return inStock.concat(soldOut)
  }

  // Assemble the /vto/generate body. Pure + null-on-missing-input so it stays
  // testable. Main garment = first tray item; the rest are additional Complete
  // Look items.
  function buildGenerateBody(opts) {
    const photoUrl = opts.photoUrl
    const items = opts.items || []
    if (!photoUrl || !items.length) return null
    const main = items[0]
    const mainImg = garmentImage(main)
    if (!mainImg) return null

    const body = {
      personImageUrl: photoUrl,
      garmentImageUrl: mainImg,
      dailyLimit: opts.dailyLimit,
    }
    // Forward the main product/variant ids so the server resolves per-product
    // config (family, category, packshot) and can NAME the primary garment in
    // the prompt. These were once omitted under the mistaken belief that extra
    // fields fail server validation — the /vto/generate schema accepts
    // productId / variantId, and without them the primary runs the unlabelled
    // legacy path.
    if (main.product && main.product.id != null) body.productId = String(main.product.id)
    if (main.variant && main.variant.id != null) body.variantId = String(main.variant.id)

    // Additional Complete Look garments go on the wire as BOTH id tuples
    // (`additionalGarments` — the labelled path the server config-resolves per
    // item) and the index-aligned `additionalGarmentImageUrls` (the flat-lay
    // fallback for an item whose product has no config row). Built from one
    // ordered pass so slot i is the same item in both; an item without a product
    // id can't be labelled and is skipped to keep the two arrays aligned.
    const garments = []
    const urls = []
    for (let i = 1; i < items.length; i++) {
      const it = items[i]
      const img = garmentImage(it)
      if (!img || !it.product || it.product.id == null) continue
      garments.push(
        it.variant && it.variant.id != null
          ? { productId: String(it.product.id), variantId: String(it.variant.id) }
          : { productId: String(it.product.id) },
      )
      urls.push(img)
    }
    if (garments.length) {
      body.additionalGarments = garments
      body.additionalGarmentImageUrls = urls
    }
    return body
  }

  // ── photo persistence (pure storage helpers) ─────────────────────────────
  // The chosen photo (uploaded or model) survives reloads within the tab so a
  // shopper never re-uploads mid-flow. sessionStorage (not local) keeps it
  // tab-scoped and self-clearing; keyed per instance so two snippets on one page
  // don't collide. All access is guarded — storage failures (private mode) or
  // corrupt JSON degrade to no-persist rather than throwing into the engine.
  function photoStorageKey(mutationHandle) {
    return `sai:${SNIPPET_ID}:photo:${mutationHandle}`
  }

  function writePhotoSession(storage, mutationHandle, state) {
    if (!state || !state.photo || !state.photo.url) return
    try {
      storage.setItem(
        photoStorageKey(mutationHandle),
        JSON.stringify({ photo: state.photo, photoR2Key: state.photoR2Key || null }),
      )
    } catch (_) {
      noop()
    }
  }

  function readPhotoSession(storage, mutationHandle) {
    try {
      const raw = storage.getItem(photoStorageKey(mutationHandle))
      if (!raw) return null
      const parsed = JSON.parse(raw)
      // sessionStorage is browser-writable, so re-validate before this URL flows
      // into <img src> and the /vto/generate body: accept only an absolute https
      // URL. A tampered/garbage entry degrades to no-saved-photo, never an
      // arbitrary src. (Stored URLs are always https — R2 publicUrl or an
      // https-upgraded Shopify CDN model image.)
      const url = parsed && parsed.photo && parsed.photo.url
      if (typeof url !== 'string' || !/^https:\/\//i.test(url)) return null
      return parsed
    } catch (_) {
      return null
    }
  }

  function clearPhotoSession(storage, mutationHandle) {
    try {
      storage.removeItem(photoStorageKey(mutationHandle))
    } catch (_) {
      noop()
    }
  }

  // Test surface — pure helpers exposed when the harness flag is set.
  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiMg6foqrk = {
      readPayload: readPayload,
      absUrl: absUrl,
      formatMoney: formatMoney,
      productOptionGroups: productOptionGroups,
      defaultSelection: defaultSelection,
      selectionImage: selectionImage,
      pickVariantBySelection: pickVariantBySelection,
      normalizeAjaxProduct: normalizeAjaxProduct,
      orderProductsOosLast: orderProductsOosLast,
      buildGenerateBody: buildGenerateBody,
      photoStorageKey: photoStorageKey,
      readPhotoSession: readPhotoSession,
      writePhotoSession: writePhotoSession,
      clearPhotoSession: clearPhotoSession,
    }
  }

  // ── Custom element ──────────────────────────────────────────────────────
  if (customElements.get(TAG)) return

  customElements.define(
    TAG,
    class extends HTMLElement {
      connectedCallback() {
        if (this._saiInit) return
        this._saiInit = true

        this._wrapper = this.closest('[data-spectrum-vis]')
        this._mutationHandle = this._wrapper?.getAttribute('data-spectrum-lq-snippet') || SNIPPET_ID
        // Proxy base resolved lazily from the Spectrum SDK (per-brand app handle,
        // e.g. /apps/spectrum_ai_<brand>) — see _proxyBaseUrl(). The attribute is a
        // dev/test override only; never a hardcoded production prefix.

        // Visibility self-guard — do no meaningful work while gated off.
        if (!this._wrapper || this._wrapper.getAttribute('data-spectrum-vis') === 'on') {
          this._init()
        } else {
          this._obs = new MutationObserver(() => {
            if (this._wrapper.getAttribute('data-spectrum-vis') === 'on') {
              this._obs.disconnect()
              this._init()
            }
          })
          this._obs.observe(this._wrapper, {
            attributes: true,
            attributeFilter: ['data-spectrum-vis'],
          })
        }
      }

      _init() {
        if (this._ready) return
        this._ready = true

        this._payload = readPayload(this) || { collections: [], props: {} }
        this._props = this._payload.props || {}
        // Store money_format / currency for client-side price formatting (prices
        // arrive as integer cents; see the SSR payload comment).
        this._moneyFormat = this._props.moneyFormat || ''
        this._currency = this._props.currency || ''
        this._collections = this._payload.collections || []
        // Flat product list across all collections (deduped by id) for tray /
        // generate / variant lookups that key by product id. The seen-map is kept
        // on the instance so products lazy-fetched later can dedupe against it.
        this._products = []
        this._seenProduct = {}
        for (const coll of this._collections) {
          // Per-collection infinite-scroll paging state. The SSR payload seeds the
          // first products; the rest are fetched from the storefront JSON endpoint
          // on demand. `_page` is the next /products.json page to request.
          coll.products = coll.products || []
          coll.handle = coll.handle || ''
          coll.productCount = typeof coll.productCount === 'number' ? coll.productCount : null
          coll._page = 1
          coll._loading = false
          coll._seen = {}
          // Done when the whole collection is already in hand, or we can't page it
          // (no handle to fetch from). Unknown count → page until a short response.
          coll._done =
            !coll.handle || (coll.productCount != null && coll.products.length >= coll.productCount)
          for (const p of coll.products) {
            coll._seen[p.id] = true
            if (this._seenProduct[p.id]) continue
            this._seenProduct[p.id] = true
            this._products.push(p)
          }
        }
        this._dailyLimit = this._props.dailyGenerationLimit || 10

        this.s = {
          step: 'photo',
          photo: null, // { url, source }
          uploading: null, // { fileName } while a photo upload is in flight
          photoR2Key: null,
          tray: [], // [{ product, variant, selection }] — selection aligned to product.options
          cat: '', // selected collection tab (collection id) — defaults to first below
          shown: PAGE_SIZE, // products revealed in the active tab (infinite scroll)
          result: null, // { imageUrl }
          added: {}, // productId -> true
          generating: false,
        }
        this._genSerial = 0

        // Default to the first collection tab + grid column config.
        this.s.cat = this._collections.length ? String(this._collections[0].id) : ''

        // Resume a photo chosen earlier this session instead of forcing a re-upload.
        // We deliberately stay on the photo step (the "you're set" card) rather
        // than jumping to products: the tray isn't persisted, so the shopper
        // re-browses products anyway, and the card confirms the photo survived
        // the reload before they continue.
        const savedPhoto = this._restorePhotoSession()
        if (savedPhoto && savedPhoto.photo && savedPhoto.photo.url) {
          this.s.photo = savedPhoto.photo
          this.s.photoR2Key = savedPhoto.photoR2Key || null
        }
        const cols = Math.max(2, Math.min(6, Number(this._props.productsPerRow) || 4))
        this.style.setProperty('--mg-cols', String(cols))
        const colsMobile = Math.max(1, Math.min(3, Number(this._props.productsPerRowMobile) || 2))
        this.style.setProperty('--mg-cols-mobile', String(colsMobile))

        this._frame = this.querySelector('.sai-mg6foqrk__frame')
        this._stepsNav = this.querySelector('.sai-mg6foqrk__steps')
        this._host = this.querySelector('[data-mg-host]')
        if (!this._host) return

        // Top bar gets a (hidden) back control prepended once.
        this._back = el('button', 'sai-mg6foqrk__back', {
          type: 'button',
          'aria-label': 'Back',
          html: '',
        })
        this._back.appendChild(svg(ICON_BACK, 18))
        this._back.addEventListener('click', () => this._goBack())
        const topbar = this.querySelector('.sai-mg6foqrk__topbar')
        if (topbar) topbar.insertBefore(this._back, topbar.firstChild)

        this._emit('opened')
        this._render()
      }

      // ── analytics ───────────────────────────────────────────────────────
      _emit(event, extra) {
        try {
          const a = window.__spectrumAi?.analytics
          if (a && typeof a.trackEvent === 'function') {
            const data = { feature: FEATURE_SLUG, snippet_id: SNIPPET_ID }
            if (extra)
              for (const k in extra)
                if (Object.prototype.hasOwnProperty.call(extra, k)) data[k] = extra[k]
            a.trackEvent(`${FEATURE_SLUG}:${event}`, data)
          }
        } catch (_) {
          noop()
        }
      }

      // ── photo persistence ───────────────────────────────────────────────────
      // Thin instance wrappers over the pure storage helpers (see
      // writePhotoSession/readPhotoSession/clearPhotoSession). The chosen photo
      // survives reloads within the tab so a shopper never re-uploads mid-flow.
      _savePhotoSession() {
        writePhotoSession(window.sessionStorage, this._mutationHandle, this.s)
      }

      _restorePhotoSession() {
        return readPhotoSession(window.sessionStorage, this._mutationHandle)
      }

      _clearPhotoSession() {
        clearPhotoSession(window.sessionStorage, this._mutationHandle)
      }

      // ── proxy ─────────────────────────────────────────────────────────────
      // Per-brand app-proxy base, e.g. "/apps/spectrum_ai_tt_vineet_dev". The
      // Spectrum app embed injects window.__spectrumAi.baseProxyUrl — the same
      // source the cart/wishlist SDKs use. Never hardcode "/apps/spectrum": the
      // proxy prefix is the brand's app handle and differs per store.
      _proxyBaseUrl() {
        // Same chain as the existing VTO snippet: SDK-injected per-brand base
        // first, then an explicit attribute override, then the platform default.
        const ai = window.__spectrumAi
        if (ai && typeof ai.baseProxyUrl === 'string' && ai.baseProxyUrl) return ai.baseProxyUrl
        return this.getAttribute('data-proxy-base') || '/apps/spectrum'
      }

      _proxyUrl(path) {
        const cid = distinctId()
        const url = this._proxyBaseUrl() + path
        if (!cid) return url
        return `${url + (url.indexOf('?') >= 0 ? '&' : '?')}cid=${encodeURIComponent(cid)}`
      }

      // ── step chrome ───────────────────────────────────────────────────────
      _stepIndex() {
        if (this.s.step === 'photo') return 0
        if (this.s.step === 'products') return 1
        return 2 // processing / result / error
      }

      _renderSteps() {
        if (!this._stepsNav) return
        const idx = this._stepIndex()
        const labels = ['Photo', 'Products', 'Result']
        const stepNames = ['photo', 'products', 'result']
        // Completed steps are clickable to jump back; never forward (can't skip
        // ahead) and never mid-generation (would abandon the in-flight request).
        const canNavigate = this.s.step !== 'processing'
        this._stepsNav.textContent = ''
        for (let i = 0; i < labels.length; i++) {
          // The terminal step (Result) reads as reached/filled once the result
          // is showing — there's no step after it, so an in-progress outline
          // would wrongly imply more to come. While generating (processing) or
          // on error it stays the active outline.
          const resultReached = i === labels.length - 1 && this.s.step === 'result'
          let cls = 'sai-mg6foqrk__step'
          if (i < idx || resultReached) cls += ' sai-mg6foqrk__step--done'
          else if (i === idx) cls += ' sai-mg6foqrk__step--active'
          const clickable = canNavigate && i < idx
          if (clickable) cls += ' sai-mg6foqrk__step--clickable'
          const step = el('span', cls)
          step.appendChild(el('span', 'sai-mg6foqrk__step-badge', { text: String(i + 1) }))
          step.appendChild(el('span', 'sai-mg6foqrk__step-label', { text: labels[i] }))
          if (clickable) {
            step.setAttribute('role', 'button')
            step.setAttribute('tabindex', '0')
            step.setAttribute('aria-label', `Go to ${labels[i]} step`)
            const go = () => this._setStep(stepNames[i])
            step.addEventListener('click', go)
            step.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                go()
              }
            })
          }
          this._stepsNav.appendChild(step)
          if (i < labels.length - 1) {
            this._stepsNav.appendChild(
              el(
                'span',
                `sai-mg6foqrk__connector${i < idx ? ' sai-mg6foqrk__connector--done' : ''}`,
              ),
            )
          }
        }
        if (this._back) {
          this._back.className = `sai-mg6foqrk__back${idx > 0 && this.s.step !== 'processing' ? ' sai-mg6foqrk__back--on' : ''}`
        }
      }

      _goBack() {
        if (this.s.step === 'products') this._setStep('photo')
        else if (this.s.step === 'result' || this.s.step === 'error') this._setStep('products')
      }

      _setStep(step) {
        this.s.step = step
        this._render()
        this._scrollToTop()
      }

      _render() {
        this._renderSteps()
        this._host.textContent = ''
        if (this.s.step === 'photo') this._renderPhoto()
        else if (this.s.step === 'products') this._renderProducts()
        else if (this.s.step === 'processing') this._renderProcessing()
        else if (this.s.step === 'result') this._renderResult()
        else if (this.s.step === 'error') this._renderError(this.s.errorMessage)
      }

      // Bring the top of the snippet into view after a step transition, so each
      // step starts at its heading rather than wherever the previous step left
      // the scroll position (e.g. the bottom CTA after a long product grid).
      // Only fires from _setStep — the initial mount renders via _render()
      // directly, so the page never auto-scrolls on load. Honors
      // prefers-reduced-motion by falling back to an instant jump.
      //
      // A sticky theme header would otherwise overlay the snippet's top (the step
      // progress bar) once block:'start' pins it to y=0. The header's height can't
      // be read reliably from the storefront — markup varies per theme and
      // reveal-on-scroll headers report 0 when parked — so the merchant supplies
      // it via the `header_offset` prop (Studio config panel). We reserve that gap
      // as scroll-margin-top. 0 (default) = no offset.
      _scrollToTop() {
        if (typeof this.scrollIntoView !== 'function') return
        const reduce =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const offset = Number(this._props.headerOffset)
        this.style.scrollMarginTop =
          Number.isFinite(offset) && offset > 0 ? `${offset}px` : ''
        requestAnimationFrame(() => {
          this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
        })
      }

      // ── Step 1 — Photo ────────────────────────────────────────────────────
      _renderPhoto() {
        const wrap = el('div', 'sai-mg6foqrk__center')
        wrap.appendChild(el('span', 'sai-mg6foqrk__eyebrow', { text: 'Step 1 — Add your photo' }))
        wrap.appendChild(
          el('h2', 'sai-mg6foqrk__heading', {
            text: this._props.heading || 'See the whole look on you',
          }),
        )
        wrap.appendChild(
          el('p', 'sai-mg6foqrk__lede', {
            text:
              this._props.subheading ||
              "Add a photo and we'll preview your selected pieces together — styled into one complete look.",
          }),
        )

        if (this.s.uploading) {
          wrap.appendChild(this._buildUploadCard())
          wrap.appendChild(
            el('p', 'sai-mg6foqrk__consent', {
              text:
                this._props.consentText ||
                'Photos are used only to generate your preview — never stored or shared.',
            }),
          )
        } else if (this.s.photo) {
          // Bordered two-column "photo ready" card (design: hasPhoto state).
          const rcard = el('div', 'sai-mg6foqrk__ready-card')
          const thumb = el('div', 'sai-mg6foqrk__ready-thumb')
          thumb.appendChild(el('img', null, { src: this.s.photo.url, alt: '' }))
          thumb.appendChild(
            el('span', 'sai-mg6foqrk__ready-label', { text: 'your photo' }),
          )
          rcard.appendChild(thumb)

          const info = el('div', 'sai-mg6foqrk__ready-info')
          const pill = el('span', 'sai-mg6foqrk__ready-pill')
          pill.appendChild(el('span', 'sai-mg6foqrk__ready-dot'))
          pill.appendChild(el('span', null, { text: 'Photo ready' }))
          info.appendChild(pill)
          info.appendChild(
            el('span', 'sai-mg6foqrk__ready-title', { text: 'Looking good — you’re set.' }),
          )
          info.appendChild(
            el('p', 'sai-mg6foqrk__ready-note', {
              text: 'Your photo stays private and is used only to generate this preview.',
            }),
          )
          const swap = el('button', 'sai-mg6foqrk__ready-swap', {
            type: 'button',
            text: 'Reupload photo',
          })
          swap.addEventListener('click', () => {
            this.s.photo = null
            this._clearPhotoSession()
            this._render()
          })
          info.appendChild(swap)
          rcard.appendChild(info)
          wrap.appendChild(rcard)
        } else {
          // Hidden inputs: browse + capture.
          const fileInput = el('input', 'sai-mg6foqrk__file-input', {
            type: 'file',
            accept: ACCEPT,
          })
          const camInput = el('input', 'sai-mg6foqrk__file-input', {
            type: 'file',
            accept: ACCEPT,
            capture: 'user',
          })
          fileInput.addEventListener('change', () => {
            if (fileInput.files?.[0]) this._uploadPhoto(fileInput.files[0])
          })
          camInput.addEventListener('change', () => {
            if (camInput.files?.[0]) this._uploadPhoto(camInput.files[0])
          })

          const dz = el('button', 'sai-mg6foqrk__dropzone', { type: 'button' })
          const dzIcon = el('span', 'sai-mg6foqrk__dropzone-icon')
          dzIcon.appendChild(svg(ICON_UPLOAD, 22))
          dz.appendChild(dzIcon)
          dz.appendChild(
            el('span', 'sai-mg6foqrk__dropzone-title', { text: 'Drag a photo here, or browse' }),
          )
          dz.appendChild(
            el('span', 'sai-mg6foqrk__dropzone-hint', { text: 'PNG or JPG · up to 10MB' }),
          )
          dz.addEventListener('click', () => {
            fileInput.click()
          })
          dz.addEventListener('dragover', (e) => {
            e.preventDefault()
          })
          dz.addEventListener('drop', (e) => {
            e.preventDefault()
            if (e.dataTransfer?.files?.[0]) this._uploadPhoto(e.dataTransfer.files[0])
          })
          wrap.appendChild(dz)

          const actions = el('div', 'sai-mg6foqrk__photo-actions')
          const cam = el('button', 'sai-mg6foqrk__btn sai-mg6foqrk__btn--ghost', { type: 'button' })
          cam.appendChild(svg(ICON_CAMERA, 16))
          cam.appendChild(el('span', null, { text: 'Take a photo' }))
          cam.addEventListener('click', () => {
            camInput.click()
          })
          actions.appendChild(cam)
          wrap.appendChild(actions)
          wrap.appendChild(fileInput)
          wrap.appendChild(camInput)

          wrap.appendChild(
            el('p', 'sai-mg6foqrk__consent', {
              text:
                this._props.consentText ||
                'Photos are used only to generate your preview — never stored or shared.',
            }),
          )
        }

        const footer = el('div', 'sai-mg6foqrk__photo-footer')
        const cont = el('button', 'sai-mg6foqrk__continue', { type: 'button', text: 'Continue →' })
        if (!this.s.photo) cont.setAttribute('disabled', '')
        cont.addEventListener('click', () => {
          if (this.s.photo) this._setStep('products')
        })
        footer.appendChild(cont)
        wrap.appendChild(footer)

        this._host.appendChild(wrap)
      }

      // Upload a freshly-picked photo (multipart POST /vto/upload-url).
      // Indeterminate progress: fetch() exposes no upload progress, so we show a
      // spinning ring (never a faked %). The card renders inline in the photo step
      // — the rest of the step (heading, consent, Continue) stays put.
      _uploadPhoto(file) {
        if (file.size > MAX_PHOTO_BYTES) {
          this._photoNotice('That photo is too large. Please use an image under 10MB.')
          return
        }
        if (file.type && !/^image\//.test(file.type)) {
          this._photoNotice('Please choose an image file.')
          return
        }
        if (!this._proxyBaseUrl()) {
          this._photoNotice(
            "Try-on isn't available on this store yet — the Spectrum app isn't connected.",
          )
          return
        }
        this.s.photo = null
        this.s.uploading = { fileName: file.name || 'your photo' }
        this._render()

        const form = new FormData()
        form.append('photo', file)
        const serial = ++this._genSerial
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
        this._uploadAbort = controller

        fetch(this._proxyUrl('/vto/upload-url'), {
          method: 'POST',
          credentials: 'same-origin',
          body: form,
          signal: controller ? controller.signal : undefined,
        })
          .then((res) => res.json().then((json) => ({ res: res, json: json })))
          .then((r) => {
            if (this._genSerial !== serial) return
            this._uploadAbort = null
            if (r.res.ok && r.json && r.json.success && r.json.data && r.json.data.publicUrl) {
              this.s.uploading = null
              this.s.photo = { url: r.json.data.publicUrl, source: 'upload' }
              this.s.photoR2Key = r.json.data.r2Key || null
              this._savePhotoSession()
              this._emit('photo_uploaded')
              this._render()
            } else {
              this._photoNotice(r.json?.error || "We couldn't upload that photo. Please try again.")
            }
          })
          .catch((err) => {
            if (this._genSerial !== serial) return
            if (err && err.name === 'AbortError') return // _cancelUpload re-renders
            this._uploadAbort = null
            this._photoNotice(
              "We couldn't reach the server. Please check your connection and try again.",
            )
          })
      }

      _cancelUpload() {
        this._genSerial++ // invalidate the in-flight response handler
        if (this._uploadAbort) {
          try {
            this._uploadAbort.abort()
          } catch (_) {
            noop()
          }
          this._uploadAbort = null
        }
        this.s.uploading = null
        this.s.photo = null
        this._render()
      }

      // Inline uploading card (replaces the dropzone while a photo uploads).
      // Indeterminate spinning ring + filename + Cancel — no progress bar.
      _buildUploadCard() {
        const u = this.s.uploading || {}
        const card = el('div', 'sai-mg6foqrk__upload-card')
        const ring = el('span', 'sai-mg6foqrk__uring')
        ring.innerHTML =
          '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true">' +
          '<circle class="sai-mg6foqrk__uring-track" cx="24" cy="24" r="20" stroke-width="5"/>' +
          '<circle class="sai-mg6foqrk__uring-arc" cx="24" cy="24" r="20" stroke-width="5" stroke-linecap="round" stroke-dasharray="34 126"/>' +
          '</svg>'
        card.appendChild(ring)
        const meta = el('div', 'sai-mg6foqrk__upload-meta')
        meta.appendChild(el('div', 'sai-mg6foqrk__upload-title', { text: 'Uploading your photo…' }))
        meta.appendChild(
          el('div', 'sai-mg6foqrk__upload-file', { text: u.fileName || 'your photo' }),
        )
        card.appendChild(meta)
        const cancel = el('button', 'sai-mg6foqrk__upload-cancel', {
          type: 'button',
          text: 'Cancel',
        })
        cancel.addEventListener('click', () => this._cancelUpload())
        card.appendChild(cancel)
        return card
      }

      _photoNotice(message) {
        this.s.photo = null
        this.s.uploading = null
        this._render()
        const notice = el('div', 'sai-mg6foqrk__notice', { text: message })
        notice.setAttribute('role', 'alert')
        this._host.insertBefore(notice, this._host.firstChild)
      }

      // ── Step 2 — Products ─────────────────────────────────────────────────
      // Tabs are the merchant's picked collections (one per collection). The grid
      // shows the active collection's products, revealed a page at a time via
      // infinite scroll.
      _collectionTabs() {
        return this._collections.map((c) => ({
          id: String(c.id),
          title: c.title || 'Collection',
        }))
      }

      _activeCollection() {
        const id = String(this.s.cat || '')
        return this._collections.find((c) => String(c.id) === id) || this._collections[0] || null
      }

      _activeProducts() {
        const coll = this._activeCollection()
        // `_done` is true once every page of the collection is loaded (or there's
        // nothing more to fetch). Until then we hold sold-out products back so
        // they can't appear above in-stock items still being fetched.
        return orderProductsOosLast((coll && coll.products) || [], !coll || !!coll._done)
      }

      // Fetch the next page of products for a collection from the storefront JSON
      // endpoint and append them (deduped, normalised). Resolves true when at
      // least one new product was added. Marks the collection done on the last
      // page, an empty/short response, a missing handle, or any fetch failure —
      // so the caller never loops. Re-entrancy is guarded by `_loading`.
      _loadMoreFromServer(coll) {
        if (!coll || coll._done || coll._loading) return Promise.resolve(false)
        coll._loading = true
        const page = coll._page || 1
        const url = `/collections/${encodeURIComponent(coll.handle)}/products.json?limit=${SERVER_PAGE_SIZE}&page=${page}`
        return fetch(url, { credentials: 'same-origin' })
          .then((res) => {
            if (!res.ok) throw new Error(`products.json ${res.status}`)
            return res.json()
          })
          .then((json) => {
            const raw = (json && json.products) || []
            coll._page = page + 1
            if (raw.length < SERVER_PAGE_SIZE) coll._done = true
            let added = 0
            for (const rp of raw) {
              if (coll._seen[rp.id]) continue
              coll._seen[rp.id] = true
              const norm = normalizeAjaxProduct(rp)
              coll.products.push(norm)
              if (!this._seenProduct[norm.id]) {
                this._seenProduct[norm.id] = true
                this._products.push(norm)
              }
              added++
            }
            if (coll.productCount != null && coll.products.length >= coll.productCount)
              coll._done = true
            return added > 0
          })
          .catch(() => {
            // Fail closed for this collection: stop paging rather than spin on a
            // broken endpoint. Already-loaded products stay browsable.
            coll._done = true
            return false
          })
          .finally(() => {
            coll._loading = false
          })
      }

      _inTray(productId) {
        return this.s.tray.some((it) => String(it.product.id) === String(productId))
      }

      _renderProducts() {
        const catalog = el('div', 'sai-mg6foqrk__catalog')

        // Collection tabs
        const cats = el('div', 'sai-mg6foqrk__cats')
        for (const tab of this._collectionTabs()) {
          const btn = el('button', 'sai-mg6foqrk__cat', {
            type: 'button',
            text: tab.title,
            'aria-pressed': String(String(this.s.cat) === tab.id),
          })
          btn.addEventListener('click', () => {
            if (String(this.s.cat) === tab.id) return
            this.s.cat = tab.id
            this.s.shown = PAGE_SIZE
            this._renderProductsBody(catalog, gridWrap)
          })
          cats.appendChild(btn)
        }
        catalog.appendChild(cats)

        // No search box: products load lazily a page at a time from the
        // collection endpoint, so a client-side title filter would only match the
        // subset already fetched — misleading "no results" for items further down
        // the collection. Reinstate only with a server-backed search over the
        // whole collection.

        const gridWrap = el('div')
        catalog.appendChild(gridWrap)

        // Tray (sticky)
        catalog.appendChild(this._buildTray())

        this._host.appendChild(catalog)
        // Kept so _addToTray can sync card states in place (no full re-render,
        // which would reset infinite scroll and jump the page).
        this._gridWrap = gridWrap
        this.s.shown = PAGE_SIZE
        this._renderProductsBody(catalog, gridWrap)
      }

      _renderProductsBody(catalog, gridWrap) {
        const list = this._activeProducts()
        const shown = Math.min(this.s.shown || PAGE_SIZE, list.length)
        gridWrap.textContent = ''
        gridWrap.appendChild(
          el('div', 'sai-mg6foqrk__count', {
            text: list.length + (list.length === 1 ? ' result' : ' results'),
          }),
        )
        const grid = el('div', 'sai-mg6foqrk__grid')
        for (let i = 0; i < shown; i++) {
          grid.appendChild(this._buildCard(list[i]))
        }
        gridWrap.appendChild(grid)

        // Infinite scroll: a sentinel below the grid loads the next page when it
        // scrolls into view. A spinner covers the work (a card build, or a server
        // fetch); without IntersectionObserver, fall back to a "Show more" button.
        //
        // Two sources feed the grid. Already-loaded products are revealed a page
        // at a time. When those run out, the rest of the collection is fetched
        // from the storefront JSON endpoint.
        this._teardownInfinite()
        const activeColl = this._activeCollection()
        const moreLoaded = shown < list.length
        const moreOnServer = !!activeColl && !activeColl._done
        if (moreLoaded || moreOnServer) {
          const sentinel = el('div', 'sai-mg6foqrk__more')
          const loadNext = () => {
            // Show a centered spinner at the bottom while the next page loads,
            // then re-render to swap it for the new cards.
            const loading = el('div', 'sai-mg6foqrk__loading')
            loading.appendChild(el('div', 'sai-mg6foqrk__spinner'))
            sentinel.replaceWith(loading)
            // Reveal one more page against the freshly-resolved list (which may
            // have grown if we fetched), then re-render.
            const reveal = () => {
              const full = this._activeProducts()
              this.s.shown = Math.min((this.s.shown || PAGE_SIZE) + PAGE_SIZE, full.length)
              this._renderProductsBody(catalog, gridWrap)
            }
            if (moreLoaded) {
              const raf = window.requestAnimationFrame || ((cb) => window.setTimeout(cb, 16))
              raf(reveal)
            } else {
              this._loadMoreFromServer(activeColl).then(reveal)
            }
          }
          if (typeof IntersectionObserver !== 'undefined') {
            const io = new IntersectionObserver((entries) => {
              for (const e of entries) {
                if (e.isIntersecting) {
                  loadNext()
                  break
                }
              }
            })
            io.observe(sentinel)
            this._infiniteObs = io
          } else {
            const more = el('button', 'sai-mg6foqrk__more-btn', {
              type: 'button',
              text: 'Show more',
            })
            more.addEventListener('click', loadNext)
            sentinel.appendChild(more)
          }
          gridWrap.appendChild(sentinel)
        }

        // refresh tab pressed states + tray
        const cats = catalog.querySelectorAll('.sai-mg6foqrk__cat')
        const tabs = this._collectionTabs()
        for (let i = 0; i < cats.length; i++)
          cats[i].setAttribute('aria-pressed', String(!!tabs[i] && tabs[i].id === String(this.s.cat)))
        this._refreshTray()
      }

      _teardownInfinite() {
        if (this._infiniteObs) {
          try {
            this._infiniteObs.disconnect()
          } catch (_) {
            noop()
          }
          this._infiniteObs = null
        }
      }

      // Format cents with this instance's store money_format / currency.
      _money(cents) {
        return formatMoney(cents, this._moneyFormat, this._currency)
      }

      // Sum of the selected variant's price across every tray item, in cents.
      // Re-resolves the variant from each item's live selection so the total
      // always reflects the exact sizes/colours the shopper picked. Returns null
      // when no item carries a price, so callers can skip the total entirely.
      _trayTotalCents() {
        let total = 0
        let any = false
        for (const it of this.s.tray) {
          const v = pickVariantBySelection(it.product, it.selection) || it.variant
          const cents = v && v.price != null ? v.price : it.product && it.product.price
          // Accept the same number|string range formatMoney does, so an item that
          // renders a price in its row always contributes to the total.
          const n = Number(cents)
          if (cents != null && cents !== '' && !Number.isNaN(n)) {
            total += n
            any = true
          }
        }
        return any ? total : null
      }

      _buildCard(p) {
        const oos = p.available === false
        const card = el('div', `sai-mg6foqrk__card${oos ? ' sai-mg6foqrk__card--oos' : ''}`)
        card.dataset.mgProductId = String(p.id)
        const media = el('div', 'sai-mg6foqrk__card-media')
        appendMedia(media, p.featuredImageUrl, p.title, 26)
        if (oos)
          media.appendChild(
            el('span', 'sai-mg6foqrk__card-tag sai-mg6foqrk__card-tag--oos', { text: 'Sold out' }),
          )
        else if (this._inTray(p.id))
          media.appendChild(el('span', 'sai-mg6foqrk__card-tag', { text: 'In tray' }))
        card.appendChild(media)
        const body = el('div', 'sai-mg6foqrk__card-body')
        const row = el('div', 'sai-mg6foqrk__card-row')
        row.appendChild(el('span', 'sai-mg6foqrk__card-title', { text: p.title || '' }))
        if (p.price != null)
          row.appendChild(el('span', 'sai-mg6foqrk__card-price', { text: this._money(p.price) }))
        body.appendChild(row)
        const added = this._inTray(p.id)
        const add = el(
          'button',
          `sai-mg6foqrk__card-add${added ? ' sai-mg6foqrk__card-add--added' : ''}`,
          {
            type: 'button',
            text: oos ? 'Sold out' : added ? 'Added · edit' : 'Add to try-on',
          },
        )
        if (oos) add.setAttribute('disabled', '')
        add.addEventListener('click', (e) => {
          e.stopPropagation()
          if (!oos) this._openDetail(p)
        })
        body.appendChild(add)
        card.appendChild(body)
        if (!oos)
          card.addEventListener('click', () => {
            this._openDetail(p)
          })
        return card
      }

      // Update the "In tray" / "Added" state of the already-rendered cards in
      // place — no grid teardown. Used after a tray change so the visible cards
      // reflect it without the repaint flash (and scroll movement) of rebuilding
      // the grid body. The badge is an absolutely-positioned overlay and the
      // button is full-width, so nothing here shifts layout.
      _syncCardStates() {
        if (!this._gridWrap) return
        const cards = this._gridWrap.querySelectorAll('.sai-mg6foqrk__card')
        for (const card of cards) {
          if (card.classList.contains('sai-mg6foqrk__card--oos')) continue
          const added = this._inTray(card.dataset.mgProductId)
          const media = card.querySelector('.sai-mg6foqrk__card-media')
          const tag = media
            ? media.querySelector('.sai-mg6foqrk__card-tag:not(.sai-mg6foqrk__card-tag--oos)')
            : null
          if (added && !tag && media) {
            media.appendChild(el('span', 'sai-mg6foqrk__card-tag', { text: 'In tray' }))
          } else if (!added && tag) {
            tag.remove()
          }
          const add = card.querySelector('.sai-mg6foqrk__card-add')
          if (add) {
            add.classList.toggle('sai-mg6foqrk__card-add--added', added)
            add.textContent = added ? 'Added · edit' : 'Add to try-on'
          }
        }
      }

      // Mount/unmount a modal sheet + lock background scroll while it's open.
      _presentSheet(sheet) {
        this._frame.appendChild(sheet)
        document.documentElement.style.overflow = 'hidden'
        document.body.style.overflow = 'hidden'
      }

      _dismissSheet(sheet) {
        sheet.remove()
        if (!this._frame.querySelector('.sai-mg6foqrk__sheet')) {
          document.documentElement.style.overflow = ''
          document.body.style.overflow = ''
        }
      }

      // Full-bleed lightbox for the result preview — image centred on a backdrop,
      // dismissed by the close button, a backdrop tap, or Escape.
      _openZoom(imageUrl) {
        if (!imageUrl) return
        const sheet = el('div', 'sai-mg6foqrk__sheet sai-mg6foqrk__zoom')
        const card = el('div', 'sai-mg6foqrk__zoom-card')

        const close = el('button', 'sai-mg6foqrk__close sai-mg6foqrk__zoom-close', {
          type: 'button',
          'aria-label': 'Close',
        })
        close.appendChild(svg(ICON_CLOSE, 16))

        const dismiss = () => {
          document.removeEventListener('keydown', onKey)
          this._dismissSheet(sheet)
        }
        const onKey = (e) => {
          if (e.key === 'Escape') dismiss()
        }

        close.addEventListener('click', dismiss)

        // The full-size image can take a moment to decode at lightbox scale; show
        // a spinner and fade the image in on load so the dialog never opens blank.
        // An already-cached preview is `complete` synchronously → reveal instantly.
        const spinner = el('span', 'sai-spinner sai-mg6foqrk__zoom-spinner')
        card.appendChild(spinner)
        const zoomImg = el('img', 'sai-mg6foqrk__zoom-img', {
          src: imageUrl,
          alt: 'Your virtual try-on',
        })
        const reveal = () => card.classList.remove('sai-mg6foqrk__zoom-card--loading')
        if (zoomImg.complete && zoomImg.naturalWidth > 0) {
          reveal()
        } else {
          card.classList.add('sai-mg6foqrk__zoom-card--loading')
          zoomImg.addEventListener('load', reveal, { once: true })
          zoomImg.addEventListener('error', reveal, { once: true })
        }
        card.appendChild(zoomImg)
        sheet.appendChild(card)
        // The close button lives on the sheet, not the card: the card runs the
        // pop animation, and a transformed ancestor would retarget the button's
        // position:fixed to the card (anchoring it to the image, then snapping to
        // the viewport corner when the transform clears). On the untransformed
        // sheet, fixed always resolves against the viewport.
        sheet.appendChild(close)
        sheet.addEventListener('click', (e) => {
          if (e.target === sheet || e.target === card) dismiss()
        })
        document.addEventListener('keydown', onKey)
        this._presentSheet(sheet)
      }

      // Whether the variant resolved by the current option selection is in stock.
      // Products can be partially available — e.g. a size is in stock under one
      // delivery tier but sold out under another — so availability is a property
      // of the resolved variant, not the product. A selection that matches no
      // variant, or matches a variant marked unavailable, counts as sold out.
      _isSelectionAvailable(product, selection) {
        if (!product) return false
        const variants = product.variants || []
        if (!variants.length) return product.available !== false
        const v = pickVariantBySelection(product, selection)
        return !!v && v.available !== false
      }

      // Product detail — two-pane modal (image left, info right); stacks on mobile.
      _openDetail(product) {
        const existing = this.s.tray.filter((it) => String(it.product.id) === String(product.id))[0]
        // The tray allows one item per collection (tab). Capture which collection
        // this selection belongs to: the active tab for a fresh add, or the item's
        // original collection when re-opening from the result step to edit options.
        const collectionId = existing ? existing.collectionId : this.s.cat
        // Working selection, aligned to product.options. Edited in place by the
        // option controls below; committed to the tray on "Add to try-on".
        const selection = existing?.selection
          ? existing.selection.slice()
          : defaultSelection(product)

        const sheet = el('div', 'sai-mg6foqrk__sheet')
        const card = el('div', 'sai-mg6foqrk__sheet-card sai-mg6foqrk__detail')

        const close = el('button', 'sai-mg6foqrk__close sai-mg6foqrk__detail-close', {
          type: 'button',
          'aria-label': 'Close',
        })
        close.appendChild(svg(ICON_CLOSE, 16))
        close.addEventListener('click', () => {
          this._dismissSheet(sheet)
        })
        card.appendChild(close)

        const media = el('div', 'sai-mg6foqrk__detail-media')
        appendMedia(media, selectionImage(product, selection), product.title, 30)
        let mediaImg = media.querySelector('img')
        card.appendChild(media)

        const info = el('div', 'sai-mg6foqrk__detail-info')
        if (product.type || product.category)
          info.appendChild(
            el('span', 'sai-mg6foqrk__detail-cat', { text: product.type || product.category }),
          )
        info.appendChild(el('h3', 'sai-mg6foqrk__detail-title', { text: product.title || '' }))
        // Price tracks the live selection: re-resolve the variant and reformat on
        // every option change so the shopper sees the exact price they'll pay.
        const priceEl = el('div', 'sai-mg6foqrk__detail-price')
        const syncPrice = () => {
          const v = pickVariantBySelection(product, selection)
          const cents = v && v.price != null ? v.price : product.price
          priceEl.textContent = cents != null ? this._money(cents) : ''
        }
        syncPrice()
        info.appendChild(priceEl)

        // Refresh the detail media to the currently-selected image-bearing value.
        const syncMedia = () => {
          const img = selectionImage(product, selection)
          if (!img) return
          if (mediaImg) {
            mediaImg.src = img
          } else {
            media.innerHTML = ''
            appendMedia(media, img, product.title, 30)
            mediaImg = media.querySelector('img')
          }
        }

        // All options, rendered generically: image-bearing options as swatches,
        // the rest as pills. Re-rendered on every change so cross-option
        // availability stays correct (no option-name matching anywhere).
        const optionsWrap = el('div', 'sai-mg6foqrk__options')
        const renderOptions = () => {
          optionsWrap.innerHTML = ''
          const groups = productOptionGroups(product, selection)
          for (const g of groups) {
            // Skip options with no real choice — an empty group, or a single
            // value (e.g. Shopify's default "Title: Default Title" for products
            // with one variant). Selection still resolves via defaultSelection.
            if (g.values.length <= 1) continue
            const chosen = selection[g.index]
            const block = el('div', 'sai-mg6foqrk__option')
            block.appendChild(
              el('div', 'sai-mg6foqrk__swatch-label', {
                html: `${g.name} · <b>${chosen != null ? chosen : ''}</b>`,
              }),
            )
            const groupWrap = el(
              'div',
              g.hasImages ? 'sai-mg6foqrk__swatches' : 'sai-mg6foqrk__sizes',
            )
            for (const val of g.values) {
              const pressed = String(val.value === chosen)
              const btn = g.hasImages
                ? el(
                    'button',
                    `sai-mg6foqrk__swatch${val.image ? ' sai-mg6foqrk__swatch--img' : ''}`,
                    {
                      type: 'button',
                      title: val.available ? val.value : `${val.value} (out of stock)`,
                      'aria-label': val.value,
                      'aria-pressed': pressed,
                    },
                  )
                : el('button', 'sai-mg6foqrk__size', {
                    type: 'button',
                    text: val.value,
                    'aria-label': `${g.name} ${val.value}${val.available ? '' : ', unavailable'}`,
                    'aria-pressed': pressed,
                  })
              if (g.hasImages && val.image) btn.style.backgroundImage = `url("${val.image}")`
              if (!val.available) btn.setAttribute('disabled', '')
              btn.addEventListener('click', () => {
                if (!val.available) return
                selection[g.index] = val.value
                renderOptions()
                syncMedia()
                syncPrice()
                syncAvailability()
              })
              groupWrap.appendChild(btn)
            }
            block.appendChild(groupWrap)
            optionsWrap.appendChild(block)
          }
        }
        renderOptions()
        info.appendChild(optionsWrap)

        const addBtn = el(
          'button',
          'sai-mg6foqrk__btn sai-mg6foqrk__btn--primary sai-mg6foqrk__btn--block sai-mg6foqrk__detail-add',
          {
            type: 'button',
            text: existing ? 'Update look' : 'Add to try-on',
          },
        )
        addBtn.addEventListener('click', () => {
          // Guard as well as disable: the selection can resolve to a sold-out
          // variant (e.g. an in-stock size under a sold-out delivery tier), and
          // an unavailable item must never reach the tray.
          if (!this._isSelectionAvailable(product, selection)) return
          this._addToTray(product, selection, collectionId)
          this._dismissSheet(sheet)
        })
        info.appendChild(addBtn)

        // Reflect the resolved variant's availability on the CTA. When the current
        // option combination is sold out, the shopper can't add it — disable and
        // relabel rather than silently letting an out-of-stock variant in.
        const syncAvailability = () => {
          const available = this._isSelectionAvailable(product, selection)
          if (available) {
            addBtn.removeAttribute('disabled')
            addBtn.textContent = existing ? 'Update look' : 'Add to try-on'
          } else {
            addBtn.setAttribute('disabled', '')
            addBtn.textContent = 'Sold out'
          }
        }
        syncAvailability()

        card.appendChild(info)
        sheet.appendChild(card)
        sheet.addEventListener('click', (e) => {
          if (e.target === sheet) this._dismissSheet(sheet)
        })
        this._presentSheet(sheet)
      }

      _addToTray(product, selection, collectionId) {
        const variant = pickVariantBySelection(product, selection)
        // Which collection (tab) this pick belongs to. Fall back to the active tab
        // so a direct call still resolves a collection.
        const collId = collectionId != null ? String(collectionId) : String(this.s.cat || '')
        const item = {
          product: product,
          variant: variant,
          selection: (selection || []).slice(),
          collectionId: collId,
        }
        // One selection per collection: drop any existing pick from the same
        // collection, unless it's this exact product (updated in place below). A
        // collection can hold mixed product types, so this keys on the collection
        // the shopper browsed, not the garment category.
        this.s.tray = this.s.tray.filter(
          (it) => String(it.collectionId) !== collId || String(it.product.id) === String(product.id),
        )
        let idx = -1
        for (let i = 0; i < this.s.tray.length; i++) {
          if (String(this.s.tray[i].product.id) === String(product.id)) {
            idx = i
            break
          }
        }
        if (idx >= 0) this.s.tray[idx] = item
        else this.s.tray.push(item)
        this._emit('tray_add', {
          product_id: String(product.id),
          collection_id: collId,
          category: categoryOf(product),
        })
        // Refresh "In tray" card states + the tray bar in place — no re-render.
        // A full _render() rebuilds the products step and resets infinite scroll
        // (this.s.shown), collapsing grid height and jumping the scroll; even
        // rebuilding just the grid body repaints every card (a brief flash). Both
        // read as a jerk when adding from the open quick-view. Mutating the existing
        // cards' badge/button avoids any teardown. Other steps (result → "Edit
        // options") still take the full render.
        if (this.s.step === 'products' && this._gridWrap) {
          this._syncCardStates()
          this._refreshTray()
        } else {
          this._render()
        }
      }

      _removeFromTray(productId) {
        this.s.tray = this.s.tray.filter((it) => String(it.product.id) !== String(productId))
        this._render()
      }

      _buildTray() {
        const tray = el('div', 'sai-mg6foqrk__tray')
        this._trayEl = tray
        this._refreshTray()
        return tray
      }

      _refreshTray() {
        const tray = this._trayEl
        if (!tray) return
        tray.textContent = ''
        // "you" photo thumb — tapping it returns to the photo step (the only
        // way back on mobile, where the top-bar back button is hidden).
        const you = el('button', 'sai-mg6foqrk__tray-you', {
          type: 'button',
          'aria-label': 'Change your photo',
        })
        if (this.s.photo) you.appendChild(el('img', null, { src: this.s.photo.url, alt: 'You' }))
        you.addEventListener('click', () => this._setStep('photo'))
        tray.appendChild(you)
        tray.appendChild(el('span', 'sai-mg6foqrk__tray-sep'))

        const items = el('div', 'sai-mg6foqrk__tray-items')
        if (!this.s.tray.length) {
          items.appendChild(
            el('span', 'sai-mg6foqrk__tray-empty', {
              text: 'Tray is empty — add an item to begin.',
            }),
          )
        } else {
          for (const it of this.s.tray) {
            const cell = el('div', 'sai-mg6foqrk__tray-item')
            appendMedia(cell, garmentImage(it), it.product.title, 16)
            const rm = el('button', 'sai-mg6foqrk__tray-remove', {
              type: 'button',
              'aria-label': `Remove ${it.product.title || 'item'}`,
            })
            rm.appendChild(svg(ICON_CLOSE, 12))
            rm.addEventListener('click', () => {
              this._removeFromTray(it.product.id)
            })
            cell.appendChild(rm)
            items.appendChild(cell)
          }
        }
        tray.appendChild(items)

        const n = this.s.tray.length
        const cta = el('button', 'sai-mg6foqrk__tray-cta', {
          type: 'button',
          text: n ? `Try it on · ${n}` : 'Try it on',
        })
        if (!n) cta.setAttribute('disabled', '')
        cta.addEventListener('click', () => {
          if (this.s.tray.length) this._startGenerate()
        })
        tray.appendChild(cta)
      }

      _toast(message) {
        const t = el('div', 'sai-mg6foqrk__notice', { text: message })
        t.setAttribute('role', 'status')
        this._host.insertBefore(t, this._host.firstChild)
        setTimeout(() => {
          t.remove()
        }, 3000)
      }

      // ── Generation ──────────────────────────────────────────────────────
      _startGenerate() {
        if (!this.s.photo || !this.s.tray.length) return
        this._setStep('processing')
        // Best-effort rate-limit pre-check; ignore failures and let generate gate.
        this._checkRateLimit().then((ok) => {
          if (ok === false) {
            this.s.errorMessage = "You've reached today's try-on limit. Please come back tomorrow."
            this._setStep('error')
            return
          }
          this._callGenerate()
        })
      }

      _checkRateLimit() {
        return fetch(this._proxyUrl(`/vto/rate-limit?limit=${this._dailyLimit}`), {
          credentials: 'same-origin',
        })
          .then((res) => res.json())
          .then((json) => {
            if (json && typeof json.remaining === 'number') return json.remaining > 0
            return true
          })
          .catch(() => true)
      }

      _renderProcessing() {
        const wrap = el('div', 'sai-mg6foqrk__processing')
        wrap.appendChild(el('div', 'sai-mg6foqrk__spinner'))
        const text = el('div', 'sai-mg6foqrk__processing-text', { text: PROCESSING_MESSAGES[0] })
        wrap.appendChild(text)
        const bar = el('div', 'sai-mg6foqrk__bar')
        const fill = el('div', 'sai-mg6foqrk__bar-fill')
        bar.appendChild(fill)
        wrap.appendChild(bar)
        this._host.appendChild(wrap)

        // Advance the bar + cycle messages until the result lands (cap 95%).
        const start = Date.now()
        const dur = 30000
        let i = 0
        this._procTimer = window.setInterval(() => {
          const p = Math.min(95, ((Date.now() - start) / dur) * 100)
          fill.style.inlineSize = `${p}%`
          const next = p < 30 ? 0 : p < 60 ? 1 : p < 85 ? 2 : 3
          if (next !== i) {
            i = next
            text.textContent = PROCESSING_MESSAGES[i]
          }
        }, 220)
      }

      _stopProcessing() {
        if (this._procTimer) {
          window.clearInterval(this._procTimer)
          this._procTimer = null
        }
      }

      _callGenerate() {
        const body = buildGenerateBody({
          photoUrl: this.s.photo?.url,
          items: this.s.tray,
          dailyLimit: this._dailyLimit,
        })
        if (!body) {
          this._stopProcessing()
          this.s.errorMessage =
            "We're missing the photo or a product image needed to generate your look."
          this._setStep('error')
          return
        }
        const serial = ++this._genSerial
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
        const timeout = window.setTimeout(() => {
          if (controller) controller.abort()
        }, GENERATE_TIMEOUT_MS)
        this._emit('generation_started', { items: this.s.tray.length })

        fetch(this._proxyUrl('/vto/generate'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller ? controller.signal : undefined,
        })
          .then((res) => res.json().then((json) => ({ res: res, json: json })))
          .then((r) => {
            if (this._genSerial !== serial) return
            if (r.res.ok && r.json && r.json.success && r.json.data && r.json.data.resultImageUrl) {
              this.s.result = { imageUrl: r.json.data.resultImageUrl }
              this._emit('generation_completed')
              this._setStep('result')
            } else {
              const code = r.json?.code || 'GENERATION_FAILED'
              let msg = r.json?.error || "We couldn't create your look right now. Please try again."
              if (code === 'RATE_LIMITED') {
                msg =
                  r.json?.error || "You've reached today's try-on limit. Please come back tomorrow."
              }
              this._emit('generation_failed', { reason: code })
              this.s.errorMessage = msg
              this._setStep('error')
            }
          })
          .catch((err) => {
            if (this._genSerial !== serial) return
            const aborted = err && err.name === 'AbortError'
            this.s.errorMessage = aborted
              ? 'That took longer than expected. Please try again.'
              : "We couldn't reach the server. Please check your connection and try again."
            this._emit('generation_failed', { reason: aborted ? 'timeout' : 'network_error' })
            this._setStep('error')
          })
          .finally(() => {
            if (this._genSerial !== serial) return
            window.clearTimeout(timeout)
            this._stopProcessing()
          })
      }

      // ── Step 3 — Result (two-pane) ────────────────────────────────────────
      _renderResult() {
        this._stopProcessing()
        const result = el('div', 'sai-mg6foqrk__result')

        // Stage — the AI preview image. Tapping it (or the zoom hint) opens a
        // full-bleed lightbox so the shopper can inspect fit/detail up close.
        const stage = el('div', 'sai-mg6foqrk__stage')
        const img = el('img', 'sai-mg6foqrk__preview', {
          src: this.s.result?.imageUrl || '',
          alt: 'Your virtual try-on',
        })
        stage.appendChild(img)
        if (this.s.result?.imageUrl) {
          const zoom = el('button', 'sai-mg6foqrk__zoom-hint', {
            type: 'button',
            'aria-label': 'Zoom in',
          })
          zoom.appendChild(svg(ICON_ZOOM, 16))
          const openZoom = () => this._openZoom(this.s.result.imageUrl)
          zoom.addEventListener('click', (e) => {
            e.stopPropagation()
            openZoom()
          })
          img.addEventListener('click', openZoom)
          stage.classList.add('sai-mg6foqrk__stage--zoomable')
          stage.appendChild(zoom)
        }
        stage.appendChild(el('span', 'sai-mg6foqrk__badge', { text: 'AI Preview' }))
        if (this._props.showBranding !== false) {
          const wm = el('a', 'sai-mg6foqrk__watermark', {
            href: 'https://getspectrum.ai',
            target: '_blank',
            rel: 'noopener noreferrer',
            'aria-label': 'Powered by Spectrum',
            html: SPECTRUM_LOGO,
          })
          stage.appendChild(wm)
        }
        result.appendChild(stage)

        // Panel — "Your look".
        const panel = el('div', 'sai-mg6foqrk__panel')
        const head = el('div', 'sai-mg6foqrk__panel-head')
        head.appendChild(el('h2', 'sai-mg6foqrk__panel-title', { text: 'Your look' }))
        head.appendChild(
          el('p', 'sai-mg6foqrk__panel-sub', {
            text:
              this.s.tray.length +
              (this.s.tray.length === 1
                ? ' item · pick a size, then add to cart'
                : ' items · pick sizes, then add to cart'),
          }),
        )
        panel.appendChild(head)

        const look = el('div', 'sai-mg6foqrk__look')
        for (const it of this.s.tray) {
          look.appendChild(this._buildRow(it))
        }
        panel.appendChild(look)

        // Footer (total + actions + disclaimer) sticks to the bottom while the
        // look list / page scrolls; the preview stage sticks to the top.
        const footer = el('div', 'sai-mg6foqrk__result-footer')

        const totalCents = this._trayTotalCents()
        if (this.s.tray.length && totalCents != null) {
          const total = el('div', 'sai-mg6foqrk__total')
          total.appendChild(el('span', 'sai-mg6foqrk__total-label', { text: 'Total' }))
          total.appendChild(
            el('span', 'sai-mg6foqrk__total-value', { text: this._money(totalCents) }),
          )
          footer.appendChild(total)
        }

        const addAll = el(
          'button',
          'sai-mg6foqrk__btn sai-mg6foqrk__btn--primary sai-mg6foqrk__btn--block sai-mg6foqrk__addall',
          {
            type: 'button',
            text: `Add all to cart · ${this.s.tray.length}`,
          },
        )
        addAll.addEventListener('click', () => {
          this._addAllToCart(addAll)
        })
        footer.appendChild(addAll)

        const actions = el('div', 'sai-mg6foqrk__result-actions')
        actions.appendChild(
          this._actionBtn('Change photo', () => {
            this.s.photo = null
            this._clearPhotoSession()
            this._setStep('photo')
          }),
        )
        actions.appendChild(
          this._actionBtn('Save / share', () => {
            this._share()
          }),
        )
        footer.appendChild(actions)

        footer.appendChild(
          el('p', 'sai-mg6foqrk__disclaimer', {
            text:
              this._props.disclaimerText ||
              'AI-generated preview. Actual fit, drape, and color may vary from the result shown.',
          }),
        )
        panel.appendChild(footer)

        result.appendChild(panel)
        this._host.appendChild(result)
      }

      _actionBtn(label, handler) {
        const b = el('button', 'sai-mg6foqrk__btn sai-mg6foqrk__btn--ghost', {
          type: 'button',
          text: label,
        })
        b.addEventListener('click', handler)
        return b
      }

      _buildRow(item) {
        const product = item.product
        const row = el('div', 'sai-mg6foqrk__row')
        const thumb = el('div', 'sai-mg6foqrk__row-thumb')
        appendMedia(thumb, garmentImage(item), item.product.title, 18)
        row.appendChild(thumb)

        // Summary column: title, chosen options + category, then price stacked
        // beneath — so the price never gets wedged beside the title on a narrow row.
        const main = el('div', 'sai-mg6foqrk__row-main')
        main.appendChild(el('span', 'sai-mg6foqrk__row-title', { text: product.title || '' }))
        // All chosen option values (Colour · Size · Material …), then category.
        const optValues = (item.selection || []).filter((v) => v != null && v !== '')
        const meta = optValues
          .concat([product.type || product.category])
          .filter(Boolean)
          .join(' · ')
        if (meta) main.appendChild(el('div', 'sai-mg6foqrk__row-meta', { text: meta }))
        const rowVariant = pickVariantBySelection(product, item.selection) || item.variant
        const rowCents = rowVariant && rowVariant.price != null ? rowVariant.price : product.price
        if (rowCents != null)
          main.appendChild(el('span', 'sai-mg6foqrk__row-price', { text: this._money(rowCents) }))

        // Options are chosen in the quick-view; tap a row to adjust them.
        const edit = el('button', 'sai-mg6foqrk__row-edit', {
          type: 'button',
          text: 'Edit options',
        })
        edit.addEventListener('click', () => {
          this._openDetail(product)
        })
        main.appendChild(edit)
        row.appendChild(main)

        // Add-to-cart is a sibling of the summary: a third column on desktop, and a
        // full-width row beneath thumb + summary on phones (see __row in the stylesheet).
        const add = el('button', 'sai-mg6foqrk__row-add', { type: 'button', text: 'Add to cart' })
        add.addEventListener('click', () => {
          this._addItemToCart(item, add)
        })
        row.appendChild(add)
        return row
      }

      _resolveVariantId(item) {
        const v = pickVariantBySelection(item.product, item.selection)
        return v ? v.id : item.variant?.id
      }

      _addItemToCart(item, btn) {
        const cart = window.Spectrum?.cart
        const variantId = this._resolveVariantId(item)
        if (!variantId || !cart || typeof cart.addAndOpen !== 'function') return
        if (btn) {
          btn.setAttribute('disabled', '')
          btn.textContent = 'Adding…'
        }
        cart
          .addAndOpen({ id: variantId, quantity: 1 })
          .then(() => {
            this._emit('add_to_cart', { product_id: String(item.product.id) })
            if (btn) {
              btn.removeAttribute('disabled')
              btn.className = 'sai-mg6foqrk__row-add sai-mg6foqrk__row-add--added'
              btn.textContent = 'Added ✓'
              // Reset so the shopper can add again — "Added" is transient feedback.
              setTimeout(() => {
                if (btn.isConnected) {
                  btn.className = 'sai-mg6foqrk__row-add'
                  btn.textContent = 'Add to cart'
                }
              }, 2000)
            }
          })
          .catch(() => {
            if (btn) {
              btn.removeAttribute('disabled')
              btn.textContent = 'Add to cart'
            }
          })
      }

      _addAllToCart(btn) {
        const cart = window.Spectrum?.cart
        if (!cart || typeof cart.addAndOpen !== 'function') return
        const items = []
        for (const it of this.s.tray) {
          const id = this._resolveVariantId(it)
          if (id) items.push({ id: id, quantity: 1 })
        }
        if (!items.length) return
        if (btn) {
          btn.setAttribute('disabled', '')
          btn.textContent = 'Adding…'
        }
        cart
          .addAndOpen(items)
          .then(() => {
            this._emit('add_all_to_cart', { items: items.length })
            if (btn) {
              btn.removeAttribute('disabled')
              btn.textContent = 'Added ✓'
              setTimeout(() => {
                if (btn.isConnected) btn.textContent = `Add all to cart · ${this.s.tray.length}`
              }, 2000)
            }
          })
          .catch(() => {
            if (btn) {
              btn.removeAttribute('disabled')
              btn.textContent = `Add all to cart · ${this.s.tray.length}`
            }
          })
      }

      _share() {
        const url = this.s.result?.imageUrl
        if (!url) return
        if (navigator.share) {
          navigator.share({ title: 'My virtual try-on', url: url }).catch(noop)
        } else {
          window.open(url, '_blank', 'noopener')
        }
        this._emit('share')
      }

      // ── Error ─────────────────────────────────────────────────────────────
      _renderError(message) {
        this._stopProcessing()
        const wrap = el('div', 'sai-mg6foqrk__error')
        wrap.appendChild(el('div', 'sai-mg6foqrk__error-icon', { text: '!' }))
        wrap.appendChild(
          el('div', 'sai-mg6foqrk__processing-text', { text: "We couldn't create your look" }),
        )
        wrap.appendChild(
          el('p', 'sai-mg6foqrk__lede', {
            text: message || 'Something went wrong while generating. Your tray is saved.',
          }),
        )
        const retry = el('button', 'sai-mg6foqrk__btn sai-mg6foqrk__btn--primary', {
          type: 'button',
          text: 'Try again',
        })
        retry.addEventListener('click', () => {
          this._startGenerate()
        })
        wrap.appendChild(retry)
        this._host.appendChild(wrap)
      }
    },
  )
})()
